import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Insights() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [criticos, setCriticos] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [montadora, setMontadora] = useState(null);
  const [empresaLoading, setEmpresaLoading] = useState(false);
  const [empresasOpts, setEmpresasOpts] = useState([]);
  const [montadorasOpts, setMontadorasOpts] = useState([]);
  const [empresaSel, setEmpresaSel] = useState("");
  const [montadoraSel, setMontadoraSel] = useState("");

  const showEmpresa = user?.role === "empresa" || isAdmin;
  const showMontadora = user?.role === "montadora" || isAdmin;

  // Carrega listas para admin
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/empresas").then(r => setEmpresasOpts(r.data || [])).catch(() => {});
    api.get("/montadoras").then(r => setMontadorasOpts(r.data || [])).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    api.get("/dashboard/produtos-criticos?dias=90&min_ocorrencias=3").then(r => setCriticos(r.data));
  }, []);

  // Empresa dashboard — recarrega quando muda seleção (admin) ou no load (empresa)
  useEffect(() => {
    if (!showEmpresa) return;
    setEmpresaLoading(true);
    const q = isAdmin && empresaSel ? `&empresa_id=${empresaSel}` : "";
    api.get(`/dashboard/empresa?dias=90${q}`)
      .then(r => setEmpresa(r.data))
      .catch(() => setEmpresa(null))
      .finally(() => setEmpresaLoading(false));
  }, [showEmpresa, isAdmin, empresaSel]);

  // Montadora dashboard — admin precisa escolher montadora
  useEffect(() => {
    if (!showMontadora) return;
    if (isAdmin && !montadoraSel) { setMontadora(null); return; }
    const q = isAdmin ? `?montadora_id=${montadoraSel}` : "";
    api.get(`/dashboard/montadora${q}`)
      .then(r => setMontadora(r.data))
      .catch(() => setMontadora(null));
  }, [showMontadora, isAdmin, montadoraSel]);

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="label-cap">Análise</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Insights</h1>
        <p className="text-xs text-muted-foreground mt-2">Dashboards consolidados — últimos 90 dias.</p>
      </div>

      <Tabs defaultValue="criticos">
        <TabsList className="rounded-none">
          <TabsTrigger value="criticos" data-testid="tab-criticos">Produtos críticos</TabsTrigger>
          {showEmpresa && <TabsTrigger value="empresa" data-testid="tab-empresa">Empresa</TabsTrigger>}
          {showMontadora && <TabsTrigger value="montadora" data-testid="tab-montadora">Montadora</TabsTrigger>}
        </TabsList>

        {/* PRODUTOS CRÍTICOS */}
        <TabsContent value="criticos" className="mt-4">
          {!criticos ? <div className="text-sm text-muted-foreground">Carregando…</div> : (
            <div>
              <div className="label-cap mb-3">
                Produtos com ≥{criticos.min_ocorrencias} assistências em {criticos.periodo_dias} dias
              </div>
              {criticos.produtos.length === 0 ? (
                <EmptyMsg msg="Nenhum produto crítico no período." />
              ) : (
                <div className="space-y-2">
                  {criticos.produtos.map((p, i) => (
                    <div key={i} className="border border-border bg-white p-4 flex justify-between items-center"
                      data-testid={`prod-critico-${i}`}>
                      <div>
                        <strong>{p.produto}</strong>
                        <div className="text-xs text-muted-foreground">{p.empresa_nome} · {p.cidades?.join(", ")}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-heading font-extrabold text-2xl text-[#E60000]">{p.ocorrencias}</div>
                        <div className="label-cap text-[10px]">assistências</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* EMPRESA — ITEM 19 */}
        {showEmpresa && (
          <TabsContent value="empresa" className="mt-4" data-testid="content-empresa">
            {isAdmin && (
              <div className="mb-4 flex items-center gap-2">
                <span className="label-cap">Empresa:</span>
                <select value={empresaSel} onChange={e => setEmpresaSel(e.target.value)}
                  className="border border-border bg-white px-3 py-1.5 text-sm"
                  data-testid="sel-empresa">
                  <option value="">Todas (agregado)</option>
                  {empresasOpts.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                </select>
              </div>
            )}
            {empresaLoading || !empresa ? (
              <div className="text-sm text-muted-foreground">Carregando…</div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-6">
                <Section title="Produtos com mais defeitos" testid="emp-produtos"
                  empty="Sem produtos com assistência no período."
                  data={empresa.produtos_problematicos}
                  render={(p, i) => (
                    <Row key={i} testid={`emp-prod-${i}`}
                      left={<strong>{p.produto}</strong>}
                      right={<RightNum n={p.n} label="assist." color="#E60000" />} />
                  )}
                />

                <Section title="Peças mais trocadas (Pulmão)" testid="emp-pecas"
                  empty="Nenhuma peça registrada no pulmão no período."
                  data={empresa.pecas_trocadas}
                  render={(p, i) => (
                    <Row key={i} testid={`emp-peca-${i}`}
                      left={<span>{p.peca}</span>}
                      right={<RightNum n={p.n} label="trocas" />} />
                  )}
                />

                <Section title="Regiões críticas" testid="emp-regioes"
                  empty="Sem regiões com assistência."
                  data={empresa.regioes_criticas}
                  render={(r, i) => {
                    const max = Math.max(...empresa.regioes_criticas.map(x => x.n), 1);
                    const w = Math.round((r.n / max) * 100);
                    return (
                      <div key={i} className="border-b border-border last:border-0 py-2" data-testid={`emp-reg-${i}`}>
                        <div className="flex justify-between text-sm">
                          <span>{r.cidade}</span>
                          <span className="font-mono">{r.n}</span>
                        </div>
                        <div className="h-1 bg-secondary mt-1">
                          <div className="h-full brand-bg" style={{ width: `${w}%` }} />
                        </div>
                      </div>
                    );
                  }}
                />

                <Section title="Montadores com mais assistência" testid="emp-montadores"
                  empty="Nenhum montador gerou assistência no período."
                  data={empresa.montadores_com_assistencia}
                  render={(m, i) => (
                    <Row key={i} testid={`emp-mont-${i}`}
                      left={
                        <div>
                          <strong>{m.nome || "—"}</strong>
                          <div className="text-xs text-muted-foreground">{m.cidade || "—"} · Score {Math.round(m.score || 0)}</div>
                        </div>
                      }
                      right={<RightNum n={m.n} label="assist." color="#E60000" />} />
                  )}
                />

                <div className="lg:col-span-2">
                  <Section title="Reincidência de clientes" testid="emp-reincidencia"
                    empty="Nenhum cliente com reincidência."
                    data={empresa.reincidencia_clientes}
                    render={(r, i) => (
                      <Row key={i} testid={`emp-rein-${i}`}
                        left={
                          <div>
                            <strong className="font-mono">{r.cpf || "—"}</strong>
                            <div className="text-xs text-muted-foreground">{(r.produtos || []).join(", ")}</div>
                          </div>
                        }
                        right={<RightNum n={r.ocorrencias} label="serviços" />} />
                    )}
                  />
                </div>
              </div>
            )}
          </TabsContent>
        )}

        {/* MONTADORA — ITEM 20 */}
        {showMontadora && (
          <TabsContent value="montadora" className="mt-4">
            {isAdmin && (
              <div className="mb-4 flex items-center gap-2">
                <span className="label-cap">Montadora:</span>
                <select value={montadoraSel} onChange={e => setMontadoraSel(e.target.value)}
                  className="border border-border bg-white px-3 py-1.5 text-sm"
                  data-testid="sel-montadora">
                  <option value="">— selecione —</option>
                  {montadorasOpts.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
                </select>
              </div>
            )}
            {!montadora ? (
              <div className="text-sm text-muted-foreground">
                {isAdmin && !montadoraSel ? "Selecione uma montadora acima para visualizar." : "Sem dados."}
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-3 gap-px bg-border border border-border">
                  <KPI label="Técnicos ativos" v={montadora.tecnicos_ativos} />
                  <KPI label="Técnicos bloqueados" v={montadora.tecnicos_bloqueados} alert={montadora.tecnicos_bloqueados > 0} />
                  <KPI label="Capacidade semanal" v={montadora.capacidade_semanal_total + "/sem"} />
                  <KPI label="Montagens abertas" v={montadora.montagens_abertas} />
                  <KPI label="Assistências abertas" v={montadora.assistencias_abertas} />
                  <KPI label="SLA 30d" v={montadora.sla_30d + "%"} highlight={montadora.sla_30d >= 80} />
                </div>
                {montadora.cidades_atendidas?.length > 0 && (
                  <div className="mt-6 border border-border bg-white p-5">
                    <div className="label-cap mb-2">Cidades atendidas ({montadora.cidades_atendidas.length})</div>
                    <div className="flex flex-wrap gap-2">
                      {montadora.cidades_atendidas.map(c => (
                        <span key={c} className="px-3 py-1 text-xs border border-border bg-secondary">{c}</span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function Section({ title, data, render, empty, testid }) {
  return (
    <div className="border border-border bg-white p-5" data-testid={testid}>
      <div className="label-cap mb-3">{title}</div>
      {!data || data.length === 0 ? (
        <div className="text-xs text-muted-foreground py-6 text-center">{empty}</div>
      ) : (
        <div>{data.map(render)}</div>
      )}
    </div>
  );
}

function Row({ left, right, testid }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-border last:border-0" data-testid={testid}>
      <div className="text-sm">{left}</div>
      <div>{right}</div>
    </div>
  );
}

function RightNum({ n, label, color }) {
  return (
    <div className="text-right">
      <div className="font-heading font-extrabold text-xl" style={color ? { color } : undefined}>{n}</div>
      <div className="label-cap text-[10px]">{label}</div>
    </div>
  );
}

function EmptyMsg({ msg }) {
  return (
    <div className="border border-dashed border-border bg-white p-12 text-center text-muted-foreground">
      {msg}
    </div>
  );
}

function KPI({ label, v, alert, highlight }) {
  return (
    <div className={`bg-white p-5 ${alert ? "border-l-4 border-l-[#E60000]" : ""} ${highlight ? "border-l-4 border-l-[#00A859]" : ""}`}>
      <div className="label-cap">{label}</div>
      <div className={`font-heading font-extrabold text-3xl mt-2 ${alert ? "text-[#E60000]" : ""}`}>{v}</div>
    </div>
  );
}
